var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var VitesseNotifications_1;
import { LitElement, html, svg, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { iconButton } from './shared';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import { groupConsecutive } from '../components/group-consecutive';
import './vitesse-icon';
// The same right-pointing chevron the nav-tree uses — rotated 90° when expanded
// (kept inline like <vitesse-nav-tree>'s so grouped notifications match the tree
// the user knows). See .grp-chevron below.
const chevron = svg `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"></polyline></svg>`;
let VitesseNotifications = class VitesseNotifications extends LitElement {
    static { VitesseNotifications_1 = this; }
    constructor() {
        super();
        this.open = false;
        this.items = [];
        this.unread = 0;
        /** Group heads (keyed by the head notification's id) the user has expanded.
         *  Groups are collapsed by default — the whole point is to tame the flood. */
        this.expanded = new Set();
        this.onChange = () => {
            this.items = this.manager?.list ?? [];
            this.unread = this.manager?.pendingCount ?? 0;
        };
        this.onDocClick = (e) => {
            // composedPath (not this.contains): we're nested inside the header's + shell's
            // shadow roots, so e.target retargets to the OUTERMOST host — this.contains
            // would be false for our own panel clicks and close on every click (incl. the
            // ✕ dismiss). composedPath crosses shadow boundaries and includes this host.
            if (!e.composedPath().includes(this))
                this.open = false;
        };
        this.onKey = (e) => {
            if (e.key === 'Escape')
                this.open = false;
        };
        new LocaleController(this);
    }
    /** (Re)subscribe whenever `manager` arrives or swaps. Idempotent — no-ops if the
     *  source is unchanged, so connectedCallback + willUpdate can both call it. */
    syncSubscription() {
        if (this.boundSource === this.manager)
            return;
        this.boundSource?.removeEventListener('change', this.onChange);
        this.boundSource = this.manager;
        this.boundSource?.addEventListener('change', this.onChange);
        this.onChange();
    }
    connectedCallback() {
        super.connectedCallback();
        this.syncSubscription();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.boundSource?.removeEventListener('change', this.onChange);
        this.boundSource = undefined;
        document.removeEventListener('click', this.onDocClick, true);
        document.removeEventListener('keydown', this.onKey);
    }
    willUpdate(changed) {
        // The source is set async after first paint; rebind when it lands/swaps.
        if (changed.has('manager'))
            this.syncSubscription();
    }
    updated(changed) {
        if (!changed.has('open'))
            return;
        if (this.open) {
            document.addEventListener('click', this.onDocClick, true);
            document.addEventListener('keydown', this.onKey);
        }
        else {
            document.removeEventListener('click', this.onDocClick, true);
            document.removeEventListener('keydown', this.onKey);
        }
    }
    toggle() {
        this.open = !this.open;
        // Opening is NOT handling. The badge follows the rows that exist (see
        // NotificationSource.pendingCount), so it clears when one is cleared - for
        // everyone - and not because this client happened to look.
    }
    act(n) {
        if (n.kind === 'claimable') {
            // Route back to the home/welcome page, where the claim banner's Claim
            // button lives (instead of popping a separate claim dialog).
            this.dispatchEvent(new CustomEvent('vitesse-home', { bubbles: true, composed: true }));
            this.open = false;
            return;
        }
        if (n.route) {
            // Host-defined navigation — forward the route string (the host interprets it) and dismiss the
            // one-shot nudge, then close the panel.
            this.dispatchEvent(new CustomEvent('vitesse-notification-action', { detail: { route: n.route, id: n.id }, bubbles: true, composed: true }));
            this.manager?.remove(n.id);
            this.open = false;
        }
    }
    dismiss(e, id) {
        e.stopPropagation();
        this.manager?.remove(id);
    }
    /** Identity of a notification for consecutive grouping: two rows are "the same"
     *  when their title AND body match (body already carries the gateway/detail), so
     *  a run of identical "Gateway came online — Gateway X" collapses, while a
     *  different gateway in between breaks the run. */
    static key(n) {
        return `${n.title}\0${n.body ?? ''}`;
    }
    toggleGroup(headId) {
        const next = new Set(this.expanded);
        next.has(headId) ? next.delete(headId) : next.add(headId);
        this.expanded = next;
    }
    /** Dismiss the whole collapsed run at once — the reason to group in the first
     *  place (one ✕ clears N identical notifications). */
    dismissGroup(e, g) {
        e.stopPropagation();
        for (const n of g.items)
            this.manager?.remove(n.id);
    }
    groupRow(g) {
        const open = this.expanded.has(g.head.id);
        const allRead = g.items.every((n) => n.read);
        return html `
      <div class="group">
        <button
          class="row group-head ${allRead ? 'read' : ''}"
          aria-expanded=${open ? 'true' : 'false'}
          aria-label=${open ? 'Collapse' : 'Expand'}
          @click=${() => this.toggleGroup(g.head.id)}
        >
          <span class="grp-chevron ${open ? 'expanded' : ''}">${chevron}</span>
          <span class="row-icon"><vitesse-icon name="hard-drive" size="16"></vitesse-icon></span>
          <span class="row-text">
            <span class="row-title">${g.head.title}</span>
            ${g.head.body ? html `<span class="row-body">${g.head.body}</span>` : nothing}
          </span>
          <span class="grp-count">${g.count}</span>
          <span
            class="row-x"
            role="button"
            title=${t('notifications.dismiss')}
            @click=${(e) => this.dismissGroup(e, g)}
          >
            <vitesse-icon name="x" size="14"></vitesse-icon>
          </span>
        </button>
        <div class="grp-children" style="grid-template-rows:${open ? '1fr' : '0fr'}">
          <div class="grp-children-overflow">${open ? g.items.map((n) => this.row(n)) : nothing}</div>
        </div>
      </div>
    `;
    }
    row(n) {
        return html `
      <button class="row ${n.read ? 'read' : ''}" @click=${() => this.act(n)}>
        <span class="row-icon"><vitesse-icon name="hard-drive" size="16"></vitesse-icon></span>
        <span class="row-text">
          <span class="row-title">${n.title}</span>
          ${n.body ? html `<span class="row-body">${n.body}</span>` : nothing}
        </span>
        <span class="row-x" role="button" title=${t('notifications.dismiss')} @click=${(e) => this.dismiss(e, n.id)}>
          <vitesse-icon name="x" size="14"></vitesse-icon>
        </span>
      </button>
    `;
    }
    render() {
        return html `
      <button class="icon-btn lg bell" title=${t('notifications.title')} aria-label=${t('notifications.title')} @click=${() => this.toggle()}>
        <vitesse-icon name="bell" size="18"></vitesse-icon>
        ${this.unread > 0 ? html `<span class="badge">${this.unread > 9 ? '9+' : this.unread}</span>` : nothing}
      </button>
      ${this.open
            ? html `<div class="panel">
            <div class="panel-head">${t('notifications.title')}</div>
            ${this.items.length === 0
                ? html `<div class="empty">${t('notifications.empty')}</div>`
                : html `<div class="list">
                  ${groupConsecutive(this.items, VitesseNotifications_1.key).map((g) => g.count === 1 ? this.row(g.head) : this.groupRow(g))}
                </div>`}
          </div>`
            : nothing}
    `;
    }
    static { this.styles = [
        iconButton,
        css `
      :host {
        position: relative;
        display: inline-flex;
      }
      .bell {
        position: relative;
      }
      .badge {
        position: absolute;
        top: -2px;
        right: -2px;
        min-width: 16px;
        height: 16px;
        padding: 0 4px;
        box-sizing: border-box;
        display: grid;
        place-items: center;
        font-size: 10px;
        font-weight: 700;
        line-height: 1;
        color: #fff;
        background: var(--red, #e0245e);
        border-radius: 8px;
      }
      .panel {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        width: 320px;
        max-height: 70vh;
        overflow-y: auto;
        background: var(--card-bg, var(--bg-secondary));
        border: 1px solid var(--border-color);
        border-radius: 12px;
        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
        z-index: 200;
        animation: pop 0.14s ease;
      }
      @keyframes pop {
        from {
          opacity: 0;
          transform: translateY(-6px) scale(0.98);
        }
      }
      .panel-head {
        padding: 12px 14px;
        font-size: 13px;
        font-weight: 600;
        color: var(--text-primary);
        border-bottom: 1px solid var(--border-color);
      }
      .empty {
        padding: 24px 14px;
        text-align: center;
        font-size: 13px;
        color: var(--text-secondary);
      }
      .list {
        display: flex;
        flex-direction: column;
      }
      .row {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 12px 14px;
        background: transparent;
        border: none;
        border-bottom: 1px solid var(--border-color);
        text-align: left;
        cursor: pointer;
        color: inherit;
        transition: background 0.12s ease;
      }
      .row:last-child {
        border-bottom: none;
      }
      .row:hover {
        background: var(--bg-tertiary, rgba(127, 127, 127, 0.08));
      }
      .row.read {
        opacity: 0.6;
      }
      .row-icon {
        flex-shrink: 0;
        display: inline-flex;
        color: var(--blue, #1a8cd8);
      }
      .row-text {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .row-title {
        font-size: 13px;
        font-weight: 600;
        color: var(--text-primary);
      }
      .row-body {
        font-size: 12px;
        color: var(--text-secondary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .row-x {
        flex-shrink: 0;
        display: inline-flex;
        color: var(--text-tertiary, var(--text-secondary));
        opacity: 0.6;
      }
      .row-x:hover {
        opacity: 1;
        color: var(--red, #e0245e);
      }

      /* ── consecutive-duplicate grouping ── */
      .group {
        display: flex;
        flex-direction: column;
        border-bottom: 1px solid var(--border-color);
      }
      .group:last-child {
        border-bottom: none;
      }
      /* the .group owns the outer divider; the head + members don't double it up */
      .group-head {
        border-bottom: none;
      }
      .grp-chevron {
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--text-secondary);
        transition: transform 0.15s ease, color 0.2s ease;
      }
      .group-head:hover .grp-chevron {
        color: var(--blue, #1a8cd8);
      }
      .grp-chevron.expanded {
        transform: rotate(90deg);
      }
      .grp-count {
        flex-shrink: 0;
        min-width: 18px;
        height: 18px;
        padding: 0 6px;
        box-sizing: border-box;
        display: grid;
        place-items: center;
        font-size: 11px;
        font-weight: 700;
        line-height: 1;
        color: var(--text-secondary);
        background: var(--bg-tertiary, rgba(127, 127, 127, 0.12));
        border-radius: 9px;
      }
      /* CSS grid-rows 1fr↔0fr expand — the same animation as <vitesse-nav-tree>. */
      .grp-children {
        display: grid;
        transition: grid-template-rows 0.2s ease;
      }
      .grp-children-overflow {
        overflow: hidden;
      }
      .grp-children .row {
        padding-left: 32px;
        border-bottom: 1px solid var(--border-color);
      }
      .grp-children .row:last-child {
        border-bottom: none;
      }
    `,
    ]; }
};
__decorate([
    property({ attribute: false })
], VitesseNotifications.prototype, "manager", void 0);
__decorate([
    state()
], VitesseNotifications.prototype, "open", void 0);
__decorate([
    state()
], VitesseNotifications.prototype, "items", void 0);
__decorate([
    state()
], VitesseNotifications.prototype, "unread", void 0);
__decorate([
    state()
], VitesseNotifications.prototype, "expanded", void 0);
VitesseNotifications = VitesseNotifications_1 = __decorate([
    customElement('vitesse-notifications')
], VitesseNotifications);
export { VitesseNotifications };
//# sourceMappingURL=vitesse-notifications.js.map